/**
 * ENG-5865 — boot-side reaper for orphaned claude -p PIDs.
 *
 * Background. The manager spawns one kind of `claude -p` child: the
 * conversation-eval backend (`manager-worker.ts`, `AGT_CONV_EVAL_BACKEND`).
 * It awaits the child's stdout to completion. When the manager is killed
 * mid-execution (agt-cli self-update restart, OOM, supervisor force-kill),
 * the child outlives the parent — re-parented to init, no longer tracked,
 * no longer awaitable, but still burning tokens until it finishes whatever
 * it was doing.
 *
 * (Historically the manager also spawned `claude -p` for scheduled-task
 * firing and direct-chat delivery, hence the original "two places" framing.
 * ENG-6347 retired both one-shots — slice 1 (ENG-6849, scheduled-task) and
 * slice 2 (ENG-6850, direct-chat) — so conv-eval is the sole survivor and
 * this reaper is scoped to it (ENG-6851). The kanban-card reset the reaper
 * used to perform for `kanban-work` orphans went with the scheduled-task
 * one-shot; the kanban route marks its own cards `error` on failure.)
 *
 * The fix per the ENG-5865 polyclaude council is recovery-side, not
 * coordination-side: persist the live PIDs to disk on every spawn; on
 * shutdown, SIGTERM them all; on the NEXT manager boot, scan the file for
 * any survivors and SIGKILL them.
 *
 * Pure module — only file I/O via injected callbacks, no signal sends, no
 * HTTP. The manager wires the real `process.kill` at the seam. This keeps
 * the testable surface free of side effects and means the reaper-decision
 * logic can be exercised in vitest with stub processes.
 */

import { existsSync, readFileSync } from 'node:fs';
import { atomicWriteFileSync } from './atomic-write.js';

export interface ClaudeSpawnRecord {
  /** OS pid of the spawned claude process. */
  pid: number;
  /** Wall-clock time (ms) the spawn was registered. */
  started_at: number;
  /**
   * Spawn provenance. The only `claude -p` spawn the manager owns is the
   * conversation-eval backend (ENG-6347 retired the scheduled-task and
   * direct-chat one-shots). The literal is kept as a single-value union so
   * any future delivery/scheduled spawn that tries to register here fails to
   * type-check instead of silently re-entering the reaper.
   */
  kind: 'conv-eval';
}

export interface ClaudePidFile {
  /** Schema version — bump when shape changes; reader tolerates mismatch by ignoring. */
  version: 1;
  /** Live spawns at write time. Removed entries are filtered out before write. */
  spawns: ClaudeSpawnRecord[];
}

/** Read the on-disk PID file. Returns an empty file on any parse/IO error. */
export function readPidFile(path: string): ClaudePidFile {
  if (!existsSync(path)) return { version: 1, spawns: [] };
  try {
    const raw = JSON.parse(readFileSync(path, 'utf-8')) as Partial<ClaudePidFile>;
    if (raw.version !== 1 || !Array.isArray(raw.spawns)) return { version: 1, spawns: [] };
    // Drop entries with non-finite pid (corrupted) but keep the rest.
    const spawns = raw.spawns.filter((s): s is ClaudeSpawnRecord =>
      !!s && typeof s.pid === 'number' && Number.isFinite(s.pid) && s.pid > 0,
    );
    return { version: 1, spawns };
  } catch {
    return { version: 1, spawns: [] };
  }
}

/** Persist via atomicWriteFileSync — readers never see a torn snapshot. */
export function writePidFile(path: string, file: ClaudePidFile): void {
  atomicWriteFileSync(path, JSON.stringify(file, null, 2));
}

/** Add a spawn to the file. Idempotent on pid collision (last write wins). */
export function addSpawn(path: string, record: ClaudeSpawnRecord): void {
  const file = readPidFile(path);
  const filtered = file.spawns.filter((s) => s.pid !== record.pid);
  writePidFile(path, { version: 1, spawns: [...filtered, record] });
}

/** Remove a spawn by pid. No-op when pid is absent (already-removed race). */
export function removeSpawn(path: string, pid: number): void {
  const file = readPidFile(path);
  const next = file.spawns.filter((s) => s.pid !== pid);
  if (next.length !== file.spawns.length) writePidFile(path, { version: 1, spawns: next });
}

export interface ReaperDecision {
  /** Spawns where the process is still alive AND identity-verified — SIGKILL'd. */
  toKill: ClaudeSpawnRecord[];
  /** Spawns where the process is already gone — only the record needs clearing. */
  alreadyDead: ClaudeSpawnRecord[];
  /**
   * CodeRabbit (PR #1631) — spawns where the PID is live but identity probe
   * says it's NOT a claude process (i.e., PID got reused by something else
   * between our SIGTERM and the next manager boot). Distinct bucket so the
   * reaper can log + skip rather than SIGKILL the wrong process. Worst case:
   * the original claude process exited cleanly between our SIGTERM and this
   * probe; we conservatively don't touch the unrelated PID.
   */
  pidReusedSkipped: ClaudeSpawnRecord[];
}

/**
 * Identity probe — returns true when the live process at `pid` is plausibly
 * one of our claude -p spawns. Defaults to `/proc/<pid>/comm` on Linux
 * (where every recent kernel exposes it), accepts any return value
 * containing 'claude' to tolerate version skew in the binary name. On
 * non-Linux hosts the probe degrades to "trust the PID" — macOS dev hosts
 * recycle PIDs much more slowly than Linux EC2, and Windows isn't a
 * supported deployment target. Injected as a callback so tests can stub
 * without touching the real filesystem.
 *
 * The pure-module contract: NO direct fs/process access from this helper;
 * the manager wires the real probe at the call site.
 */
export type ClaudeIdentityProbe = (pid: number) => boolean;

/**
 * Decide what the boot reaper should do given the on-disk PID file. Pure —
 * `isAlive` and `looksLikeClaude` are injected so tests can stub without
 * actually probing live PIDs.
 *
 * Three-way partition (CodeRabbit PR #1631):
 *   - alive AND looks-like-claude → toKill (SIGKILL it)
 *   - alive but NOT claude → pidReusedSkipped (PID reuse — don't touch)
 *   - not alive → alreadyDead (just clear the record)
 */
export function decideReaperActions(
  file: ClaudePidFile,
  isAlive: (pid: number) => boolean,
  looksLikeClaude: ClaudeIdentityProbe = () => true,
): ReaperDecision {
  const toKill: ClaudeSpawnRecord[] = [];
  const alreadyDead: ClaudeSpawnRecord[] = [];
  const pidReusedSkipped: ClaudeSpawnRecord[] = [];
  for (const s of file.spawns) {
    if (!isAlive(s.pid)) { alreadyDead.push(s); continue; }
    if (!looksLikeClaude(s.pid)) { pidReusedSkipped.push(s); continue; }
    toKill.push(s);
  }
  return { toKill, alreadyDead, pidReusedSkipped };
}

/**
 * Format the structured `[drain] ...` log line emitted on shutdown. Kept
 * here so the line shape stays unit-testable (operators grep on the prefix).
 */
export function formatDrainShutdownLine(opts: {
  step: string;
  elapsedMs: number;
  killedPids?: number[];
  note?: string;
}): string {
  const killed = opts.killedPids && opts.killedPids.length > 0
    ? ` killed_pids=${opts.killedPids.join(',')}`
    : '';
  const note = opts.note ? ` note="${opts.note.replace(/"/g, '\\"')}"` : '';
  return `[drain] step=${opts.step} elapsed_ms=${opts.elapsedMs}${killed}${note}`;
}

/** Format the boot-side reaper outcome line. Same prefix; greppable. */
export function formatReaperBootLine(opts: {
  totalRecorded: number;
  killed: number;
  alreadyDead: number;
  pidReusedSkipped: number;
}): string {
  return (
    `[drain] step=boot-reaper total=${opts.totalRecorded} ` +
    `killed=${opts.killed} already_dead=${opts.alreadyDead} ` +
    `pid_reused_skipped=${opts.pidReusedSkipped}`
  );
}
